import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPinIcon,
  CalendarIcon,
  BeakerIcon,
  BuildingOfficeIcon,
  UserIcon,
  PhoneIcon,
  TagIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  HeartIcon,
  ShieldExclamationIcon,
  BugAntIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';

const PlantDetailPage = () => {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [plantData, setPlantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchPlantDetails();
  }, [plantId]);

  const fetchPlantDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPlantPublic(plantId);

      if (response.success) {
        setPlantData(response.data);
      } else {
        setError(response.message || 'Failed to fetch plant details');
        toast.error(response.message || 'Failed to fetch plant details');
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while fetching plant details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleReportClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setShowReportModal(true);
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    toast.success('Thank you for your report. It has been submitted for review.');
  };

  const handleLoginRedirect = () => {
    setShowLoginPrompt(false);
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  };

  const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-6 w-6 text-green-600 mr-2" />}
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start py-2">
      {Icon && <Icon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 mt-1">{value || 'N/A'}</dd>
      </div>
    </div>
  );

  const StatusBadge = ({ status, type }) => {
    let colorClass = 'bg-gray-100 text-gray-800';

    if (type === 'health') {
      if (status === 'healthy') colorClass = 'bg-green-100 text-green-800';
      else if (status === 'mild_stress') colorClass = 'bg-yellow-100 text-yellow-800';
      else if (status === 'moderate_stress') colorClass = 'bg-orange-100 text-orange-800';
      else if (status === 'severe_stress') colorClass = 'bg-red-100 text-red-800';
    } else if (type === 'market') {
      if (status === 'on_track') colorClass = 'bg-green-100 text-green-800';
      else if (status === 'at_risk') colorClass = 'bg-yellow-100 text-yellow-800';
      else if (status === 'lost') colorClass = 'bg-red-100 text-red-800';
    } else if (type === 'age') {
      if (status === 'new') colorClass = 'bg-blue-100 text-blue-800';
      else if (status === 'mature') colorClass = 'bg-green-100 text-green-800';
      else if (status === 'old') colorClass = 'bg-gray-100 text-gray-800';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {formatFieldName(status)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading plant details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Plant Details</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/plants')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Plants
          </button>
        </div>
      </div>
    );
  }

  if (!plantData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Plant Not Found</h2>
          <p className="text-gray-600 mb-4">The requested plant could not be found.</p>
          <button
            onClick={() => navigate('/plants')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Plants
          </button>
        </div>
      </div>
    );
  }

  const { plant, bed, plot, farm } = plantData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate(-1)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Back
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">Plant Details</li>
            </ol>
          </nav>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {plant.plant_common_name}
              </h1>
              <p className="text-gray-600 mt-1 italic">
                {plant.scientific_name}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Plant ID: {plant.id}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Report Button */}
              <button
                onClick={handleReportClick}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors shadow-sm"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Update
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Plant Information */}
            <InfoCard title="Plant Information" icon={SparklesIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Common Name" value={plant.plant_common_name} icon={TagIcon} />
                <InfoRow label="Scientific Name" value={plant.scientific_name} />
                <InfoRow label="Native Region" value={plant.native_region} icon={MapPinIcon} />
                <InfoRow label="Harvest Type" value={formatFieldName(plant.harvest_type)} />

                <div className="flex items-start py-2">
                  <HeartIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500">Health Status</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      <StatusBadge status={plant.health_status} type="health" />
                    </dd>
                  </div>
                </div>

                <div className="flex items-start py-2">
                  <SparklesIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500">Plant Age Class</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      <StatusBadge status={plant.plant_age_class} type="age" />
                    </dd>
                  </div>
                </div>

                <InfoRow label="Growth Stage" value={formatFieldName(plant.growth_stage)} />

                <div className="flex items-start py-2">
                  <ShieldExclamationIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500">Market Relevance</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      <StatusBadge status={plant.market_relevance} type="market" />
                    </dd>
                  </div>
                </div>

                <InfoRow label="Created" value={formatDate(plant.created_at)} icon={CalendarIcon} />
                <InfoRow label="Last Updated" value={formatDate(plant.updated_at)} />
              </dl>
            </InfoCard>

            {/* Stress & Damage Information */}
            <InfoCard title="Stress & Damage Information" icon={BugAntIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Primary Stress Indicator" value={formatFieldName(plant.primary_stress_indicator)} />
                <InfoRow label="Damage Source" value={formatFieldName(plant.damage_source)} />
                <InfoRow label="Damage Location" value={formatFieldName(plant.damage_location)} />
                <InfoRow label="Field Context" value={formatFieldName(plant.field_context)} />
                <InfoRow label="Intervention Status" value={formatFieldName(plant.intervention_status)} />
              </dl>
            </InfoCard>

            {/* Bed Information */}
            <InfoCard title="Bed Information" icon={BeakerIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Bed Number" value={bed?.bed_number} icon={TagIcon} />
                <InfoRow label="Crop Type" value={bed?.crop} />
                <InfoRow label="Bed Size" value={bed?.size ? `${bed.size} sq ft` : 'N/A'} icon={MapPinIcon} />
                <InfoRow label="Seed Type" value={bed?.seed} />
                <InfoRow label="Seed Source" value={bed?.source} />
                <InfoRow label="Planting Date" value={formatDate(bed?.date)} icon={CalendarIcon} />
              </dl>
            </InfoCard>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Plot Information */}
            <InfoCard title="Plot Information" icon={MapPinIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Plot Name" value={plot?.name} />
                <InfoRow label="Description" value={plot?.description} />
                <InfoRow label="Area" value={plot?.area ? `${plot.area} acres` : 'N/A'} />
                <InfoRow
                  label="Coordinates"
                  value={plot?.coordinates ?
                    `${plot.coordinates.latitude}°N, ${plot.coordinates.longitude}°E` :
                    'N/A'
                  }
                />
                <InfoRow label="Created" value={formatDate(plot?.created_at)} icon={CalendarIcon} />
              </dl>
            </InfoCard>

            {/* Farm Information */}
            <InfoCard title="Farm Information" icon={BuildingOfficeIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Farm Name" value={farm?.name} />
                <InfoRow label="Description" value={farm?.description} />
                <InfoRow label="Owner" value={farm?.owner} icon={UserIcon} />
                <InfoRow label="Contact" value={farm?.contact} icon={PhoneIcon} />
                <InfoRow label="Total Area" value={farm?.area ? `${farm.area} acres` : 'N/A'} />
                <InfoRow label="Location" value={farm ? `${farm.address}, ${farm.region}, ${farm.country}` : 'N/A'} />
                <InfoRow
                  label="Coordinates"
                  value={farm?.coordinates && (farm.coordinates.latitude || farm.coordinates.longitude) ?
                    `${farm.coordinates.latitude}°N, ${farm.coordinates.longitude}°E` :
                    'N/A'
                  }
                />
                <InfoRow label="Established" value={formatDate(farm?.created_at)} icon={CalendarIcon} />
              </dl>
            </InfoCard>

            {/* QR Code Section */}
            {plant.qr_code && (
              <InfoCard title="QR Code">
                <div className="text-center">
                  <img
                    src={plant.qr_code}
                    alt="Plant QR Code"
                    className="mx-auto h-32 w-32 border border-gray-200 rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Scan to view this plant's details
                  </p>
                </div>
              </InfoCard>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/plants')}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors font-medium"
          >
            ← Back to Plants
          </button>
        </div>
      </div>

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Report an Issue"
        size="xl"
      >
        <ReportForm
          plantData={plantData}
          onSuccess={handleReportSuccess}
          onCancel={() => setShowReportModal(false)}
        />
      </Modal>

      {/* Login Prompt Modal */}
      <Modal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Login Required"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLoginRedirect}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Login to Continue
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            You need to be logged in to report issues. Please log in to continue.
          </p>
          <p className="text-sm text-gray-500">
            After logging in, you'll be redirected back to this page to submit your report.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default PlantDetailPage;
